import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, UserPlus, Link2, Copy, Trash2, Check, Circle } from "lucide-react";
import { colors } from "../theme";
import { projectsApi } from "../api/projects";

// Owner-facing share panel for a saved project: invite collaborators by email (editor/viewer),
// generate a shareable invite link, see who's active, and manage access. Sequential collaboration —
// collaborators open the same project, edit and save; the owner stays in control of access.
export default function CollaborationShare({ project, onClose }) {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [linkRole, setLinkRole] = useState("editor");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try { setInfo(await projectsApi.collaborators(project.id)); }
    catch { setInfo({ my_role: "owner", collaborators: [], active: [] }); }
  }, [project.id]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000); // poll presence / roster while open
    return () => clearInterval(t);
  }, [refresh]);

  const isOwner = info?.my_role === "owner";
  const link = info?.invite_code ? `${window.location.origin}/?join=${info.invite_code}` : "";

  const add = async () => {
    setErr(""); setBusy(true);
    try {
      const e = email.trim();
      if (!e || !e.includes("@")) { setErr("Enter a valid email"); setBusy(false); return; }
      await projectsApi.addCollaborator(project.id, e, role);
      setEmail(""); await refresh();
    } catch { setErr("Could not add — are you the owner?"); }
    setBusy(false);
  };
  const remove = async (e) => { await projectsApi.removeCollaborator(project.id, e); refresh(); };
  const genLink = async () => {
    setBusy(true);
    try { await projectsApi.inviteLink(project.id, linkRole); await refresh(); } catch { setErr("Could not create link"); }
    setBusy(false);
  };
  const copyLink = () => {
    if (!link) return;
    navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const active = new Set((info?.active || []).map((a) => (a || "").toLowerCase()));

  return createPortal(
    <div style={s.overlay} data-testid="share-modal" onClick={onClose}>
      <div style={s.card} onClick={(e) => e.stopPropagation()}>
        <button style={s.close} onClick={onClose} data-testid="share-close"><X size={18} /></button>
        <div style={s.title}>Share “{project.name || "project"}”</div>
        <div style={s.sub}>Invite people to work on this session together. Invitees must sign in; changes are saved to the shared project so you can build on each other's work.</div>

        {isOwner ? (
          <>
            <div style={s.sectionTitle}>Invite by email</div>
            <div style={s.inviteRow}>
              <input style={s.input} value={email} placeholder="name@email.com" data-testid="share-email"
                onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
              <select style={s.select} value={role} onChange={(e) => setRole(e.target.value)} data-testid="share-role">
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <button style={s.addBtn} onClick={add} disabled={busy} data-testid="share-add">
                <UserPlus size={14} /> Add
              </button>
            </div>
            {err && <div style={s.err} data-testid="share-error">{err}</div>}

            <div style={s.sectionTitle}>Invite link</div>
            {link ? (
              <div style={s.linkRow}>
                <input style={{ ...s.input, flex: 1 }} readOnly value={link} data-testid="share-link" />
                <button style={s.iconBtn} onClick={copyLink} data-testid="share-copy">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
              </div>
            ) : (
              <div style={s.inviteRow}>
                <select style={s.select} value={linkRole} onChange={(e) => setLinkRole(e.target.value)} data-testid="share-link-role">
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button style={s.addBtn} onClick={genLink} disabled={busy} data-testid="share-gen-link"><Link2 size={14} /> Create link</button>
              </div>
            )}
            {link && <div style={s.hint}>Anyone signed in who opens this link joins as <b>{info?.invite_role}</b>.</div>}
          </>
        ) : (
          <div style={s.hint}>You have <b>{info?.my_role}</b> access to this shared project{info?.owner_email ? ` (owner: ${info.owner_email})` : ""}.</div>
        )}

        <div style={s.sectionTitle}>People ({(info?.collaborators || []).length + 1})</div>
        <div style={s.list} data-testid="share-people">
          <Person email={info?.owner_email || "Owner"} role="owner" active={active.has((info?.owner_email || "").toLowerCase())} />
          {(info?.collaborators || []).map((c) => (
            <Person key={c.email} email={c.email} role={c.role} active={active.has((c.email || "").toLowerCase())}
              onRemove={isOwner ? () => remove(c.email) : null} />
          ))}
        </div>
        {info?.last_editor_email && <div style={s.hint}>Last edited by <b>{info.last_editor_email}</b>.</div>}
      </div>
    </div>, document.body);
}

const Person = ({ email, role, active, onRemove }) => (
  <div style={s.person} data-testid={`share-person-${email}`}>
    <Circle size={9} fill={active ? "#34c759" : "transparent"} color={active ? "#34c759" : colors.border} />
    <span style={s.personEmail}>{email}</span>
    <span style={s.roleBadge(role)}>{role}</span>
    {onRemove && <button style={s.rmBtn} onClick={onRemove} data-testid={`share-remove-${email}`}><Trash2 size={13} /></button>}
  </div>
);

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,32,0.55)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4200 },
  card: { position: "relative", width: 480, maxWidth: "94vw", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 20, padding: 28, boxShadow: "0 24px 70px rgba(0,0,0,0.3)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" },
  close: { position: "absolute", top: 14, right: 14, border: "none", background: "transparent", cursor: "pointer", color: colors.textTertiary },
  title: { fontSize: 19, fontWeight: 800, color: colors.textPrimary, marginBottom: 4 },
  sub: { fontSize: 12.5, color: colors.textTertiary, marginBottom: 16, lineHeight: 1.5 },
  sectionTitle: { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: colors.textTertiary, margin: "14px 0 8px" },
  inviteRow: { display: "flex", gap: 8, alignItems: "center" },
  linkRow: { display: "flex", gap: 8, alignItems: "center" },
  input: { flex: 1, border: `1.5px solid ${colors.border}`, borderRadius: 10, padding: "9px 11px", fontSize: 13, color: colors.textPrimary, outline: "none", minWidth: 0 },
  select: { border: `1.5px solid ${colors.border}`, borderRadius: 10, padding: "9px 8px", fontSize: 13, color: colors.textPrimary, background: "#fff", cursor: "pointer" },
  addBtn: { display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: colors.brand, color: "#fff", borderRadius: 10, padding: "9px 13px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  iconBtn: { border: `1.5px solid ${colors.border}`, background: "#fff", borderRadius: 10, padding: "9px 11px", cursor: "pointer", color: colors.textSecondary },
  err: { fontSize: 12, color: "#d9534f", marginTop: 6 },
  hint: { fontSize: 11.5, color: colors.textTertiary, marginTop: 8, lineHeight: 1.5 },
  list: { display: "flex", flexDirection: "column", gap: 6, marginTop: 2 },
  person: { display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", background: "#f6f8fa", borderRadius: 10 },
  personEmail: { flex: 1, fontSize: 12.5, color: colors.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  roleBadge: (role) => ({ fontSize: 9.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, padding: "2px 8px", borderRadius: 999, color: "#fff", background: role === "owner" ? colors.brand : role === "viewer" ? "#8a8a8f" : "#2a6fd6" }),
  rmBtn: { border: "none", background: "transparent", cursor: "pointer", color: colors.textTertiary, padding: 2 },
};
